import { observable, makeObservable, reaction, } from 'mobx';
import { FieldDesc, FormStore } from '../FormStore';
import { get } from 'lodash'
import { ValueObj } from '../data/FormData';
import { BaseField } from './BaseField';
import { runInAction } from 'mobx';

type ReferReturnArr = {
  [key: string]: string
}

type ReturnInfo = {
  storeKey: string
  fieldAlias: string
  referFieldAlias: string
}

enum ROW_STATUS {
  INSERT = 'Insert',
  DELETE = 'Delete',
  UNCHANGED = 'Unchanged',
  UPDATE = 'Update'
}

type ReturnArr = ReturnInfo[]

enum SELECT_TYPE {
  MULTIPLE = 'multiple',
  SINGLE = 'single'
}

export interface ReferFieldDesc extends FieldDesc {
  refReturnArr: string
  refReturn: string[]
  isMasterOrg: boolean
  selectType: SELECT_TYPE
  dataSourceAlias: string
  characterEffects: string[] // 特征副作用
}

const jsonParse = (jsonStr: string | object) => {
  if (typeof jsonStr === 'object') {
    return jsonStr
  } else if (typeof jsonStr === 'string') {
    try {
      return JSON.parse(jsonStr)
    } catch (e) {
      console.log(e)
      return {}
    }
  }

}
/**
 * 参照field
 * 参照带入
 * 多选参照存值/取值
 */
export class ReferField extends BaseField {
  refReturnArr: ReferReturnArr
  returnArr: ReturnArr
  refReturn: string[]
  selectType: SELECT_TYPE
  characterEffects?: string[]
  dataSourceAlias: string
  multipleReferValue: any

  constructor(fieldDesc: ReferFieldDesc, formStore: FormStore) {
    super(fieldDesc, formStore)
    this.refReturnArr = jsonParse(fieldDesc.refReturnArr)
    this.returnArr = this.adaptRefReturnArr(this.refReturnArr)
    this.refReturn = fieldDesc.refReturn
    this.selectType = fieldDesc.selectType || SELECT_TYPE.SINGLE
    this.dataSourceAlias = fieldDesc.dataSourceAlias
    this.multipleReferValue = []
    /**
     * 当前参照有特征副作用, 用参照id值和特征组的类型(bizType)批量请求特征组信息
     * 批量请求只在浏览态进行, 编辑态的特征组信息在点击特征组，弹出弹窗时请求
     **/
    makeObservable(this, {
      multipleReferValue: observable
    })
    if (fieldDesc.characterEffects) {
      // 关联的特征组字段
      this.characterEffects = fieldDesc.characterEffects
      if (!this.formStore.characterEffects) {
        this.formStore.characterEffects = {}
      }
      this.formStore.characterEffects[this.fieldAlias] = this.characterEffects
    }


    if (fieldDesc.selectType === SELECT_TYPE.MULTIPLE) {
      this.multipleReferValue = this.formStore.data[this.dataSourceAlias] || []
      reaction(
        () => this.formStore.data[this.dataSourceAlias],
        () => {
          this.multipleReferValue = this.formStore.data[this.dataSourceAlias] || []
        }
      )
    }
    this.addCharacterFields()
  }

  private addCharacterFields() {
    if (this.isCharacter()) {
      this.returnArr?.forEach((item: any) => {
        this.formStore.fields.set(item.fieldAlias, this.formStore.fieldFactory.createField({ ...item, stateType: 'character' }))
      })
    }
  }

  private isSingle() {
    return this.selectType === SELECT_TYPE.SINGLE
  }

  /**
   * 参照onChange调用
   * @param referValue 参照onChange值
   * @param checkMasterOrg 是否要弹出主组织确认窗
   */
  async setValue(referValue: ValueObj, checkMasterOrg: boolean = true) {
    if (!referValue) {
      console.error(`referVaule的值是:${referValue}`)
      referValue = {}
    }
    const isContinue = await this.useHook('beforeSetValue', referValue)
    if (isContinue === false) return false
    const oldValue = this.formStore.getValue(this.fieldAlias)
    const oldNameValue = this.formStore.getValue(`${this.fieldAlias}_name`)
    // 是否是主组织字段
    const isMaster = this.fieldAlias === this.formStore.masterOrgField
    // 是否是交易类型字段
    const isTransType = this.fieldAlias === this.formStore.transTypeField

    // 1. checkMasterOrg:是否要弹主组织变化的提示窗 2. oldValue && oldValue !== referValue.id 主组织id字段不为空且发生变化 3. oldNameValue && oldNameValue !== referValue.name 主组织name字段不为空且发生变化
    // 以下3个if条件将name字段一起判断,主要是因为如果主组织或交易类型是被其他参照带入的话,setValue方法调用之前,id字段就已经填入了,但是name字段还没填入,需要用或条件一起判断
    if (checkMasterOrg && isMaster && oldValue && oldValue !== referValue.id) {
      this.formStore.rootStore.utils.confirm({
        title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80045', '切换主组织将清空数据，是否继续'),
        fieldidPrefix: `${this.formStore.rootStore.pageStore.params.billNo}|${this.fieldAlias}`,
        onOk: () => {
          this.setData(referValue)
          this.formStore.setMasterOrgValue(this.formStore.getValue(this.formStore.masterOrgField), this.formStore.getValue(this.formStore.masterOrgNameField))
        }
      });
    } else if (isMaster && (oldValue !== referValue.id || oldNameValue !== referValue.name)) {
      this.setData(referValue)
      this.formStore.setMasterOrgValue(this.formStore.getValue(this.formStore.masterOrgField), this.formStore.getValue(this.formStore.masterOrgNameField))
    } else if (isTransType && (oldValue !== referValue.id || oldNameValue !== referValue.name)) {
      this.setData(referValue)
      this.formStore.setTransTypeValue(this.formStore.getValue(this.formStore.transTypeField), this.formStore.getValue(this.formStore.transTypeNameField))
    } else {
      this.setData(referValue)
    }
    this.validate()
  }

  /**
   * 向store存值
   * 单选-按照带入配置向formStore的其他字段存值
   * 多选
   * **按照tableStoreKey向虚拟子实体的tableStore存值
   * **查询区-不做特殊处理，将组件传值以storeField作为key值存储
   * @param referValue
   */
  setData(referValue: ValueObj) {
    if (typeof referValue === 'string' && arguments.length === 1) {
      this.setDetailData(referValue)
    } else if (Array.isArray(referValue)) {
      this.setMultipleData(referValue)
    } else {
      // 单选参照
      this.setReturnField(referValue)
    }
  }
  public getField() {
    return this.isSingle() ? (this.field || this.fieldAlias) : this.dataSourceAlias
  }

  // detail接口返回值设置
  setDetailData(referValue: string) {
    this.formStore.setTrueValue(this.fieldAlias, referValue, this.isCharacter())
  }

  // 多选设置
  setMultipleData(referValue: ValueObj[]) {
    // 多选参照
    if (!referValue.length) {
      this.multipleReferValue = this.diffUpdate([])
      this.formStore.data[this.dataSourceAlias] = this.multipleReferValue
      return
    }
    let fieldAlias = this.fieldAlias
    // 查询区，存值取值不做转换
    if (this.isFilter) {
      this.formStore.setTrueValue(fieldAlias, referValue, this.isCharacter())
    } else {
      let selectAll: any[] = []
      referValue.forEach((value: any) => {
        const returnValue: { [key: string]: string } = {}
        for (const returnInfo of this.returnArr) {
          const { fieldAlias, referFieldAlias } = returnInfo
          returnValue[fieldAlias] = value?.[referFieldAlias] || ''
        }
        selectAll.push(returnValue)
      })
      this.multipleReferValue = this.diffUpdate(selectAll)
      this.formStore.data[this.dataSourceAlias] = this.multipleReferValue
    }
  }
  // 差量更新
  diffUpdate(rows: ValueObj[]) {
    // 更新旧值的状态位
    this.multipleReferValue.forEach((item: any, index: string | number) => {
      const findIndex = rows.findIndex((row) =>
        Object.entries(row).every(
          ([key, value]) => item[key] === value
        )
      );
      if (findIndex === -1) {
        // 新值中不存在，旧值中存在
        // 如果是新增态数据，删除
        if (this.multipleReferValue[index]?._status === ROW_STATUS.INSERT) {
          delete this.multipleReferValue[index]
        } else {
          // 其他状态的数据，状态位置为'Delete'
          this.multipleReferValue[index]._status = ROW_STATUS.DELETE
        }
        // delete
      } else {
        // @ts-ignore
        const _status: ROW_STATUS = this.multipleReferValue[index]._status
        // 新值旧值中都存在, 状态位置为'Update'
        if ([ROW_STATUS.UNCHANGED, undefined, ROW_STATUS.DELETE].includes(_status)) {
          this.multipleReferValue[index]._status = ROW_STATUS.UPDATE
        }
        rows.splice(findIndex, 1);
      }
    })

    // 插入数据的状态位
    rows.forEach(row => row._status = ROW_STATUS.INSERT)
    // 旧数据中已删除的数据过滤
    const updatedValue = this.multipleReferValue.filter((value: any) => value !== undefined)
    return updatedValue.concat(rows)
  }


  adaptRefReturnArr = (refReturnArr: ReferReturnArr) => {
    const returnArr: ReturnArr = []
    for (const referFieldAlias in refReturnArr) {
      // 要带入的字段
      const returnField = refReturnArr[referFieldAlias]
      // 要将一个值带入到多个字段, 字段之间用会;分割
      returnField.split(';').forEach(item => {
        const returnFieldArr = item.split(',')
        returnArr.push({
          storeKey: returnFieldArr[0],
          fieldAlias: returnFieldArr[1],
          referFieldAlias: referFieldAlias
        })
      })
    }
    return returnArr
  }

  /**
   * 参照带入, 将参照值带入store中
   * @param referValue 参照onChange值
   */
  setReturnField = (referValue: ValueObj) => {
    try {
      let returnStore
      // 参照带入 && 扩展
      runInAction(() => {
        for (const returnInfo of this.returnArr) {
          const { storeKey, fieldAlias, referFieldAlias } = returnInfo
          const rootStore = this?.formStore?.rootStore
          returnStore = rootStore?.[storeKey] || this?.formStore
          const returnValue = get(referValue, referFieldAlias) || null
          if(fieldAlias == this.fieldAlias){
            returnStore.setTrueValue(this.getField(), returnValue, this.isCharacter())
            continue
          }
          const key = returnStore?.get(fieldAlias)?.getField() || fieldAlias
          returnStore.setTrueValue(key, returnValue, returnStore.get(fieldAlias)?.isCharacter())
        }
      })


    } catch (e) {
      console.log(e)
    }
  }

  /**
   * 清除参照值
   * 单选参照, 清除当前字段和带入字段（触发与参照带入相同的逻辑）
   * 多选参照
   * ** 查询区-同单选
   * ** 子表-不处理，走子表逻辑
   */
  clear = () => {
    if (this.fieldAlias)
      // @ts-ignore
      this.setReturnField(null)
    if (this.dataSourceAlias)
      this.formStore.setTrueValue(this.dataSourceAlias, [], this.isCharacter())
    this.multipleReferValue = []
    // 清空带入字段
    // if (this.selectType === SELECT_TYPE.SINGLE) this.setReturnField({})
  }

  /**
   * 参照组件接收的值，从store中取值，转换为参照组件需要的值
   * 单选参照-从带入的字段中取值，用带入信息中的referFieldAlias组装为参照需要的值
   * 多选参照
   * **子表存值方式，从虚拟实体的tableStore中取值，用带入信息拼装为组件所需值
   * **查询区, 用storeField从formStore中取值
   */
  get value() {
    if (this.selectType === 'multiple') {
      return this.getMultipleValue()
    } else {
      return this.getSingleValue()
    }
  }

  // 获取单选参照值传给组件
  getSingleValue = () => {
    const referValue: ValueObj = {}
    for (const returnInfo of this.returnArr) {
      const { storeKey, fieldAlias, referFieldAlias } = returnInfo
      // 参照name取值时取：参照（兼容财务），参照Name（兼容财务），参照_name，排除配置了带入的字段
      if (referFieldAlias === 'name' && [this.fieldAlias, `${this.fieldAlias}Name`, `${this.fieldAlias}_name`].indexOf(fieldAlias) === -1) continue
      const rootStore = this.formStore.rootStore
      const returnStore = rootStore[storeKey] || this.formStore
      const StoreValue = returnStore.getValue(fieldAlias, this.characterId)
      if (StoreValue) referValue[referFieldAlias] = StoreValue
    }

    return referValue
  }

  // 获取多选参照值传给组件
  getMultipleValue = () => {
    if (this.isFilter) {
      // 查询区，存值取值不做转换
      // return this.formStore.getValue(this.fieldAlias)
      return this.getValue()
    } else {
      const rows = this.multipleReferValue.filter((item: any) => item._status !== ROW_STATUS.DELETE)
      return rows.map((row: any) => {
        const referValue: { [key: string]: string } = {}
        for (const returnInfo of this.returnArr) {
          const { fieldAlias, referFieldAlias } = returnInfo
          // 参照name取值时取：参照（兼容财务），参照Name（兼容财务），参照_name，排除配置了带入的字段
          if (referFieldAlias === 'name' && [this.fieldAlias, `${this.fieldAlias}Name`, `${this.fieldAlias}_name`].indexOf(fieldAlias) === -1) continue
          const returnValue = row[fieldAlias] || ''
          referValue[referFieldAlias] = returnValue
        }
        return referValue
      })
    }
  }

  get flowFieldAuth() {
    if (!this.field) return
    // 找到主表的formStore
    let form = this.formStore
    // 如果是移动端子表的formStore的话，需要通过tableStore定位到主表的formStore
    if (this.formStore.tableStoreKey) {
      const table = this.formStore.rootStore[this.formStore.tableStoreKey]
      form = this.formStore.rootStore[table.formStoreKey]
    }
    // 没有流程实例id，或者驳回到制单的状态，或者没有配置过流程字段权限，就不用管字段权限
    if (!form.procinstId || form.verifyState == 4 || !form.workFlowStore.currentTaskAuth) return

    const defaultAuth = { visible: true, editable: false }
    if (this.formStore.tableStoreKey) {
      // 移动端子表编辑页的情况
      return form.workFlowStore.currentTaskAuth[this.formStore.dataSource]?.[this.field] || defaultAuth
    } else{
      // 主表的情况
      // 多选参照
      if (!this.isSingle()) {
        return form.workFlowStore.currentTaskAuth[this.dataSourceAlias]?.[this.field] || defaultAuth
      }
      // 单选参照
      return form.workFlowStore.currentTaskAuth.mainTable?.[this.field] || defaultAuth
    }
  }
}
